/**
 * Social Security constants (spec §8). The FOUNDATION uses the FRA table,
 * claiming adjustment formula, and COLA to turn a stated benefit-at-FRA into an
 * annual benefit. Full PIA-from-earnings, spousal, survivor, earnings-test
 * recomputation, and the optimizer grid come in phase 10.
 *
 * Sources: SSA.gov (FRA table, reduction/credit formulas); SSA 2026 COLA fact
 * sheet (2.8%); 2025 OASDI Trustees Report (depletion 2033, 77% payable).
 * PIA bend points ($1,286 / $7,749 for 2026 eligibility) flagged for
 * primary-source confirmation before use in the phase-10 earnings-history path.
 */

export const SS_CONSTANTS_AS_OF = '2026-08-03';

/** Most recent COLA (2026), applied to benefits in payment. */
export const COLA_2026 = 0.028;

/** Full Retirement Age in months, by birth year. */
export function fraMonths(birthYear: number): number {
  if (birthYear <= 1937) return 65 * 12;
  if (birthYear <= 1942) return 65 * 12 + (birthYear - 1937) * 2; // 65 + 2mo/yr
  if (birthYear <= 1954) return 66 * 12;
  if (birthYear <= 1959) return 66 * 12 + (birthYear - 1954) * 2; // 66 + 2mo/yr
  return 67 * 12;
}

/**
 * Benefit multiplier vs. PIA for claiming at `claimAgeMonths`, given FRA.
 *   - Early: 5/9 of 1% per month for the first 36 months before FRA,
 *            then 5/12 of 1% per month beyond.
 *   - Delayed: 8%/yr (2/3 of 1% per month) from FRA to age 70; capped at 70.
 */
export function claimingMultiplier(claimAgeMonths: number, fraMo: number): number {
  const capped = Math.min(claimAgeMonths, 70 * 12);
  if (capped === fraMo) return 1;

  if (capped < fraMo) {
    const monthsEarly = fraMo - capped;
    const first36 = Math.min(monthsEarly, 36);
    const beyond = Math.max(monthsEarly - 36, 0);
    const reduction = first36 * (5 / 9 / 100) + beyond * (5 / 12 / 100);
    return 1 - reduction;
  }

  const monthsDelayed = capped - fraMo;
  return 1 + monthsDelayed * (2 / 3 / 100);
}

/** 2026 earnings test limits (spec §8). */
export const EARNINGS_TEST_2026 = {
  underFraAnnual: 24_480, // $1 withheld per $2 over
  fraYearAnnual: 65_160, // $1 withheld per $3 over, months before FRA
};
